<template>
  <div class="container mx-auto p-5">
    <div class="flex items-center justify-center">
      <div class="w-full lg:w-2/3 mt-10 mb-10">
        <div class="shadow-lg rounded-lg bg-white">
          <div class="p-5">
            <!-- Back Arrow -->
            <div class="mb-5">
              <div
                class="bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center"
              >
                <router-link to="/mrem-masterclass">
                  <i class="fa-solid fa-circle-arrow-left text-gray-600"></i>
                </router-link>
              </div>
            </div>
            <!-- Logo -->
            <div class="flex justify-center mb-5">
              <router-link to="/">
                <img
                  src="../assets/images/BM_Blue.svg"
                  alt="MMC Logo"
                  class="h-16"
                />
              </router-link>
            </div>
            <!-- Title -->
            <h3 class="text-center text-3xl font-bold text-gray-800 mb-8">
              MREM Masterclass Registration
            </h3>
            <!-- Display amount -->
            <div
              v-if="selectedPlan"
              class="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 shadow-sm"
            >
              <p class="text-sm text-gray-500 text-center">
                You're subscribing to:
              </p>
              <p class="text-2xl font-semibold text-gray-900 text-center mt-1">
                {{ selectedPlan.name }}
              </p>
              <p class="text-lg font-medium text-indigo-600 text-center mt-2">
                Payment Amount: {{ selectedPlan.cost }}
              </p>
            </div>

            <!-- Form -->
            <form @submit.prevent="submitRegistration" class="space-y-6 mt-5">
              <!-- Full Name and Email -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label
                    for="Fname"
                    class="block text-sm font-medium text-gray-700"
                    >Full Name</label
                  >
                  <input
                    type="text"
                    id="Fname"
                    placeholder="Enter full name"
                    v-model="registrationForm.full_name"
                    class="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  />
                  <p
                    v-if="!registrationForm.full_name && isTouched.full_name"
                    class="text-red-500 text-sm"
                  >
                    * Full name is required
                  </p>
                </div>
                <div>
                  <label
                    for="email"
                    class="block text-sm font-medium text-gray-700"
                    >Email Address</label
                  >
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter email address"
                    v-model="registrationForm.email"
                    class="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  />
                  <p
                    v-if="!registrationForm.email && isTouched.email"
                    class="text-red-500 text-sm"
                  >
                    * Email is required
                  </p>
                </div>
              </div>
              <!-- Phone Number and Gender -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label
                    for="phoneNumber"
                    class="block text-sm font-medium text-gray-700"
                    >Phone Number</label
                  >
                  <input
                    type="text"
                    id="phoneNumber"
                    placeholder="Enter phone number"
                    v-model="registrationForm.phone_number"
                    class="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  />
                  <p
                    v-if="
                      !registrationForm.phone_number && isTouched.phone_number
                    "
                    class="text-red-500 text-sm"
                  >
                    * Phone number is required
                  </p>
                </div>
                <div>
                  <label
                    for="gender"
                    class="block text-sm font-medium text-gray-700"
                    >Gender</label
                  >
                  <select
                    id="gender"
                    v-model="registrationForm.gender"
                    class="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option disabled selected>Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  <p
                    v-if="!registrationForm.gender && isTouched.gender"
                    class="text-red-500 text-sm"
                  >
                    * Gender is required
                  </p>
                </div>
              </div>
              <!-- Remaining Fields -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label
                    for="occupation"
                    class="block text-sm font-medium text-gray-700"
                    >Occupation</label
                  >
                  <input
                    type="text"
                    id="occupation"
                    placeholder="Enter occupation"
                    v-model="registrationForm.occupation"
                    class="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  />
                  <p
                    v-if="!registrationForm.occupation && isTouched.occupation"
                    class="text-red-500 text-sm"
                  >
                    * Occupation is required
                  </p>
                </div>
                <div>
                  <label
                    for="businessSector"
                    class="block text-sm font-medium text-gray-700"
                    >Business Sector Industry</label
                  >
                  <select
                    id="businessSector"
                    v-model="registrationForm.business_sector_industry"
                    class="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option disabled selected>Select Business Sector</option>
                    <option
                      v-for="(sector, index) in businessData"
                      :key="index"
                      :value="sector"
                    >
                      {{ sector }}
                    </option>
                  </select>
                  <p
                    v-if="
                      !registrationForm.business_sector_industry &&
                      isTouched.business_sector_industry
                    "
                    class="text-red-500 text-sm"
                  >
                    * Business sector is required
                  </p>
                </div>
              </div>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label
                    for="qualification"
                    class="block text-sm font-medium text-gray-700"
                    >Qualification</label
                  >
                  <select
                    id="qualification"
                    v-model="registrationForm.qualification"
                    class="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option disabled selected>Select Qualification</option>
                    <option value="bsc">BSc</option>
                    <option value="msc">MSc</option>
                    <option value="phd">PhD</option>
                    <option value="diploma">Diploma</option>
                    <option value="ssce">SSCE</option>
                  </select>
                  <p
                    v-if="
                      !registrationForm.qualification && isTouched.qualification
                    "
                    class="text-red-500 text-sm"
                  >
                    * Qualification is required
                  </p>
                </div>
                <div>
                  <label
                    for="businessType"
                    class="block text-sm font-medium text-gray-700"
                    >Business Type</label
                  >
                  <input
                    type="text"
                    id="businessType"
                    placeholder="Enter business type"
                    v-model="registrationForm.business_type"
                    class="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  />
                  <p
                    v-if="
                      !registrationForm.business_type && isTouched.business_type
                    "
                    class="text-red-500 text-sm"
                  >
                    * Business type is required
                  </p>
                </div>
              </div>
              <!-- Expectations and Address -->
              <div>
                <label
                  for="challenge"
                  class="block text-sm font-medium text-gray-700"
                  >What are your expectations
                  <span class="text-xs text-red-500"
                    >(Your response should not exceed 250 words)*
                  </span></label
                >
                <textarea
                  id="challenge"
                  rows="5"
                  placeholder="Enter your expectations"
                  v-model="registrationForm.expectations"
                  @input="handleChallengeInput"
                  class="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                ></textarea>
                <p
                  class="text-sm mt-1"
                  :class="{
                    'text-red-600': wordCount <= 250,
                    'text-green-600': wordCount > 250,
                  }"
                >
                  {{ wordCount }} / 250
                </p>
              </div>
              <div>
                <label
                  for="stateOfResidence"
                  class="block text-sm font-medium text-gray-700"
                  >State of Residence</label
                >
                <textarea
                  id="stateOfResidence"
                  rows="5"
                  placeholder="Enter your state of residence"
                  v-model="registrationForm.state_of_residence"
                  class="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                ></textarea>
                <p
                  v-if="
                    !registrationForm.state_of_residence &&
                    isTouched.state_of_residence
                  "
                  class="text-red-500 text-sm"
                >
                  * State of residence is required
                </p>
              </div>
              <!-- Submit Button -->
              <div class="flex justify-center">
                <button
                  type="submit"
                  class="bg-gradient text-white rounded px-4 py-2 transition-transform hover:scale-110 focus:outline-none"
                >
                  Register
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import apiService from "../services/apiService";
import {
  showErrorAlert,
  showLoadingAlert,
  showSuccessAlert,
} from "../services/reusableService";

export default {
  props: ["id", "cost"],
  data() {
    return {
      selectedPlan: null,
      wordCount: 0,
      registrationForm: {
        full_name: "",
        email: "",
        phone_number: "",
        gender: "",
        occupation: "",
        business_sector_industry: "",
        business_type: "",
        qualification: "",
        expectations: "",
        state_of_residence: "",
        training_type_id: "", // Dynamically assigned in mounted
      },
      isTouched: {
        full_name: false,
        email: false,
        phone_number: false,
        gender: false,
        occupation: false,
        business_sector_industry: false,
        business_type: false,
        qualification: false,
        expectations: false,
        state_of_residence: false,
      },
      businessData: [],
    };
  },
  methods: {
    async fetchTrainingData() {
      const response = await apiService.getTrainingTypeMrem();
      this.trainingData = response.data.data;
      console.log("Training Data:", this.trainingData);
      const paramId = this.$route.params.id;
      const matchedItem = this.trainingData.find((item) => item.id == paramId);
      if (matchedItem) {
        console.log("Matched Training Item:", matchedItem);
        this.selectedPlan = matchedItem;
      }
    },
    async fetchBusinessSectors() {
      try {
        const response = await apiService.getBusinessSector();
        this.businessData = response.data.data || [];
      } catch (error) {
        console.error("Failed to fetch business sectors:", error);
      }
    },
    async submitRegistration() {
      try {
        showLoadingAlert("Processing your registration...");

        this.markAllTouched();

        const formData = new FormData();
        Object.keys(this.registrationForm).forEach((key) => {
          formData.append(key, this.registrationForm[key]);
        });

        const response = await apiService.createRegistration(formData);

        // Validate form and response
        if (
          response &&
          response.data &&
          response.data.status === true &&
          this.isFormValid()
        ) {
          console.log("Registration successful:", response.data);

          const amount = Number(this.registrationForm.cost); // Adjust if amount is nested differently

          // Call Paystack only if amount is greater than 0
          if (amount > 0) {
            this.payWithPaystack();
          } else {
            showSuccessAlert("Registration is successful!");
            this.resetForm();
          }
        } else {
          console.warn("Response does not indicate success:", response);
          showErrorAlert("Registration was not successful. Please try again.");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          "Failed to submit registration. Please try again.";
        console.error("Error during registration submission:", error);
        showErrorAlert(errorMessage);
      } finally {
        console.log("Swal closed");
      }
    },
    convertNairaToKobo(nairaStr) {
      const cleanStr = nairaStr.replace(/[N,₦\s]/g, "");
      const nairaAmount = parseInt(cleanStr);
      const koboAmount = nairaAmount * 100;
      console.log("AMOUNT", koboAmount);
      return koboAmount;
    },
    handleChallengeInput() {
      this.wordCount = this.countWords(this.registrationForm.challenges);
      if (this.wordCount > 250) {
        // Trim the text to first 250 words
        const words = this.registrationForm.challenges.trim().split(/\s+/);
        this.registrationForm.challenges = words.slice(0, 250).join(" ");
        this.wordCount = 250;
        showErrorAlert("You can only enter up to 250 words.");
      }
    },
    countWords(text) {
      if (!text) return 0;
      const words = text
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0);
      return words.length;
    },
    payWithPaystack() {
      console.log(this.submitRegistration);
      const handler = PaystackPop.setup({
        key: "pk_live_b264657a54fd2e8f20af228849e2ad813d24d936", // Live public key
        email: this.registrationForm.email,
        amount: this.convertNairaToKobo(this.selectedPlan.cost), // Amount in kobo (₦1000)
        currency: "NGN",
        ref: `MSHEL-${Date.now()}`,
        metadata: {
          custom_fields: [
            {
              display_name: this.registrationForm.full_name,
              variable_name: "phone_number",
              value: this.registrationForm.phone_number,
            },
          ],
        },
        callback: (response) => {
          console.log("Payment successful:", response);
          showSuccessAlert(
            "Payment successful! Welcome to Mshel Mentorship Club."
          );
          this.resetForm();
          this.$router.push("/success");
        },
        onClose: () => {
          showErrorAlert("Payment was cancelled.");
        },
      });

      handler.openIframe();
    },
    markAllTouched() {
      for (let key in this.isTouched) {
        this.isTouched[key] = true;
      }
    },
    isFormValid() {
      return Object.values(this.registrationForm).every((field) => field);
    },
    resetForm() {
      this.registrationForm = {
        full_name: "",
        email: "",
        phone_number: "",
        gender: "",
        occupation: "",
        business_sector_industry: "",
        business_type: "",
        qualification: "",
        expectations: "",
        state_of_residence: "",
        training_type_id: this.$route.params.id || "",
      };
    },
  },
  mounted() {
    this.registrationForm.training_type_id = this.$route.params.id || "";
    this.fetchBusinessSectors();
    this.selectedPlan = history.state?.item || null;
    this.fetchTrainingData();
  },
};
</script>

<style scoped>
.text-3xl {
  font-size: 24px !important;
}
</style>
